// The personnel sync check's vocabulary: what each issue code the server returns
// (core/personnel_sync.py) is called, and what can be done about it. Kept free of
// React so the page and the Excel export read the same words.

export const ISSUES = {
  incomplete:              { label: "Missing role or qualification", side: "db" },
  no_email:                { label: "No email address", side: "db" },
  not_in_db:               { label: "Not in database", side: "db" },
  mc_missing:              { label: "Not in MailChimp", side: "mailchimp" },
  mc_not_subscribed:       { label: "MailChimp: not subscribed", side: "mailchimp" },
  mc_groups:               { label: "MailChimp groups differ", side: "mailchimp" },
  mc_unexpected:           { label: "In MailChimp, shouldn't be", side: "mailchimp" },
  o365_missing:            { label: "No O365 account", side: "o365" },
  o365_not_in_group:       { label: "Not in web app group", side: "o365" },
  o365_disabled:           { label: "O365 account disabled", side: "o365" },
  o365_unexpected_access:  { label: "In web app group, shouldn't be", side: "o365" },
  o365_unexpected_account: { label: "O365 account, shouldn't have", side: "o365" },
};

export const issueLabel = (code) => ISSUES[code]?.label || code;

// The fixes the app can make itself. `bulk` ones only ever *add* — a contact, a
// group, sign-in access — and are idempotent, so they're the ones offered across
// many rows at once. Archiving is one row at a time, and removing sign-in is done
// in the Entra portal (the row links there).
export const FIXES = {
  mc_add:     { label: "Add to MailChimp", bulk: true },
  mc_sync:    { label: "Sync MailChimp groups", bulk: true },
  mc_archive: { label: "Archive in MailChimp", bulk: false },
  o365_grant: { label: "Grant O365 access", bulk: true },
};

// The fix for one issue on one row, or null when it needs a person (open the
// record, or the portal).
export const fixFor = (code, row) => {
  switch (code) {
    case "mc_missing": return "mc_add";
    case "mc_groups": return "mc_sync";
    case "mc_unexpected": return "mc_archive";
    case "not_in_db": return row.mailchimp?.subscribed ? "mc_archive" : null;
    case "o365_missing":
    case "o365_not_in_group": return "o365_grant";
    default: return null;
  }
};

// Every distinct fix a row offers (a row can need two issues fixed by one call,
// e.g. no account *and* so not in the group).
export const fixesFor = (row) =>
  [...new Set(row.issues.map(i => fixFor(i.code, row)).filter(Boolean))];

const lower = (s) => (s || "").toLowerCase();

// The rows the filters leave. `issues` and `statuses` are OR within each; an
// empty list doesn't filter. "(none)" in `statuses` is the rows with no record.
export const filterRows = (rows, { issues = [], statuses = [], text = "", withIssuesOnly = true } = {}) => {
  const q = lower(text).trim();
  return rows.filter(r =>
    (!withIssuesOnly || r.issues.length > 0)
    && (issues.length === 0 || r.issues.some(i => issues.includes(i.code)))
    && (statuses.length === 0 || statuses.includes(r.status || "(none)"))
    && (!q || [r.name, r.email, r.login_email, r.private_notes].some(v => lower(v).includes(q))));
};

const yesNo = (v) => (v == null ? "" : v ? "Yes" : "No");
const list = (xs) => (xs || []).join(", ");

export const mailchimpText = (r) => (r.mailchimp ? r.mailchimp.status : "Not present");
export const o365Text = (r) => (r.o365
  ? [r.o365.user_type || "User",
     r.o365.external_user_state === "PendingAcceptance" ? "invite pending" : null,
     r.o365.account_enabled === false ? "disabled" : null].filter(Boolean).join(", ")
  : "Not present");

// One spreadsheet row; the keys are the column headers.
export const syncSheetRow = (r) => ({
  "Name": r.name || "",
  "Email": r.email || "",
  "Sign-in address": r.login_email || "",
  "Status": r.status || "(not in database)",
  "Discrepancies": r.issues.map(i => i.text).join("; "),
  "Should be in MailChimp": r.expected ? yesNo(r.expected.mailchimp) : "",
  "Should be in O365": r.expected ? yesNo(r.expected.o365) : "",
  "In database": r.personnel_id ? "Yes" : "No",
  "MailChimp": mailchimpText(r),
  "O365": o365Text(r),
  "In web app group": r.o365 ? yesNo(r.o365.in_group) : "No",
  "Roles (database)": list(r.roles),
  "Roles (MailChimp)": list(r.mailchimp?.roles),
  "Qualifications (database)": list(r.qualifications),
  "Qualifications (MailChimp)": list(r.mailchimp?.qualifications),
  "Private notes": r.private_notes || "",
  "Related record": r.related?.name || "",
});
